import { Component, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { filter, take } from "rxjs/operators";

import { UserService, ExchangeService, WindowService, BaseComponent } from "@mypxplat/xplat/core";
import { AppService, AvoService, FirebaseService } from "@mypxplat/xplat/web/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-my-fender-welcome",
  templateUrl: "my-fender-welcome.component.html",
  standalone: false,
})
export class MyFenderWelcomeComponent extends BaseComponent implements OnDestroy {
  public step: number = 1;
  public isInitialLoad: boolean = true;
  public contentVisible: boolean = false;
  public showRestOfUI: boolean = false;
  public isFirstContentShow: boolean = true;
  public stepsArray: any[] = [1, 2, 3, 4, 5];
  private _ctaTracked: boolean = false;
  public steps: Record<string, any> = {
    1: {
      header: this.translateService.instant("my_fender_welcome.welcome_to_myfender"),
      content: `<div class='t-20 bold text-center m-y-20'>${this.translateService.instant("my_fender_welcome.step1")}</div>`,
    },
    2: {
      header: this.translateService.instant("my_gear.register_your_gear"),
      content: `<div class='t-20 bold text-center m-y-20'>${this.translateService.instant("my_fender_welcome.step2")}</div><div class="d-flex align-items-end"><img class="p-x-10" src="./assets/images/register-fender-gear.png" alt="Fender Gear" /><img class="p-x-10" src="./assets/images/fsp8-images/fsp-subscription-box.png" alt="PreSonus Software" /><img class="p-x-10" src="./assets/images/register-presonus-hardware.png" alt="PreSonus Hardware" /></div>`,
    },
    3: {
      header: this.translateService.instant("my_fender_welcome.learn_from_best"),
      content: `<div class='t-20 bold text-center m-y-20'>${this.translateService.instant("my_fender_welcome.step3")}</div><div class="d-flex align-items-end"><img src="https://i.ytimg.com/vi/mKn0gMH7qOs/hqdefault.jpg" class="p-x-10 border-radius10" /><img src="https://i.ytimg.com/vi/2n291QtNnqw/hqdefault.jpg" class="p-x-10 border-radius10" /><img src="https://i.ytimg.com/vi/oDAknaJ9Kpo/hqdefault.jpg" class="p-x-10 border-radius10" /></div>`,
    },
    4: {
      header: this.translateService.instant("my_fender_welcome.connect_with_others"),
      content: `<div class='t-20 bold text-center m-y-20'>${this.translateService.instant("my_fender_welcome.step4")}</div><img src="${this.appService.activeTheme == "dark" ? "./assets/images/community-example-dark.png" : "./assets/images/community-example-light.png"}" />`,
    },
    5: {
      header: this.translateService.instant("education.get_started"),
      content: "",
    },
  };
  public initialAnimationDone: boolean = false;
  public shouldAnimateInFromRight: boolean = false;
  public shouldAnimateInFromLeft: boolean = false;
  constructor(
    userService: UserService,
    public windowService: WindowService,
    public activeModal: NgbActiveModal,
    public exchangeService: ExchangeService,
    public appService: AppService,
    public db: AngularFirestore,
    public translateService: TranslateService,
    public fbService: FirebaseService,
    private avoService: AvoService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.userService.user?.presonus_cognito_user_id) {
      let imgSrc = this.appService.activeTheme == "dark" ? "./assets/images/presonus_logo.png" : "./assets/images/presonus_logo_black.png";
      this.steps[1].content += `
        <div class="m-b-20 m-t-30 text-center">
          <img src="${imgSrc}" style="width: 100px; height: auto; margin: auto;" /><br />
          ${this.translateService.instant("my_fender_welcome.myfender_replaces_mypresonus")}
        </div>
      `;
    }
    this.windowService.setTimeout(() => {
      this.initialAnimationDone = true;
    }, 2000);

    if (this.user?.id) {
      this.fbService.doc("user_metadata/" + this.user.id).set(
        {
          sphere_announcements: {
            my_fender_welcome: true,
          },
        },
        { merge: true }
      );
    }

    // Landing Pop Up: fire Viewed as Navigation Clicked (only in My Fender)
    // Audit requires: navigation_group: '', navigation_type: 'Community', navigation_flyout: 'community', navigation_label: 'All Channels', navigation_core: 'myfender'
    if (this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof te.navigationClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Navigation" });
            const extra = {
              navigationGroup: "",
              navigationType: "sub" as const, // Using 'sub' as 'Community' is not a valid NavigationType value, but navigationFlyout indicates it's Community
              navigationFlyout: "community",
              navigationLabel: "All Channels",
              navigationCore: "myfender",
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            te.navigationClicked(payload);
          }
        });
    }
  }

  /** Fire CTA Clicked for Landing Pop Up then dismiss (Let's go or Skip). */
  private fireLandingPopUpCta(ctaText: string) {
    this._ctaTracked = true; // Mark that a CTA was tracked
    if (this.userService.appId !== "fender") {
      this.activeModal.dismiss();
      return;
    }
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (te && typeof te.ctaClicked === "function") {
          const page = this.avoService.getPageProperties({ pageName: "Landing Pop Up" });
          const extra: Record<string, any> = {
            ctaText,
            ctaLocation: "Landing Pop Up",
            utmId: (page as any).utmId ?? 0,
            utmTerm: (page as any).utmTerm ?? "",
            userId_: this.user?.["fc-id"] ?? undefined,
            fcId: this.user?.["fc-id"] ?? undefined,
          };
          const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
          te.ctaClicked(payload as any);
        }
        this.activeModal.dismiss();
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy?.();
    // Track close action if modal was closed without clicking a CTA (backdrop click or ESC)
    if (!this._ctaTracked && this.userService.appId === "fender") {
      this.avoService.avoInitted$
        .pipe(
          filter((v) => !!v),
          take(1)
        )
        .subscribe(() => {
          const te = this.avoService.trackEvent();
          if (te && typeof te.ctaClicked === "function") {
            const page = this.avoService.getPageProperties({ pageName: "Landing Pop Up" });
            const extra: Record<string, any> = {
              ctaText: "Close",
              ctaLocation: "Landing Pop Up",
              utmId: (page as any).utmId ?? 0,
              utmTerm: (page as any).utmTerm ?? "",
              userId_: this.user?.["fc-id"] ?? undefined,
              fcId: this.user?.["fc-id"] ?? undefined,
            };
            const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
            te.ctaClicked(payload as any);
          }
        });
    }
  }

  onLetsGoClick() {
    this.fireLandingPopUpCta("Let's go");
  }

  onSkipClick() {
    this.fireLandingPopUpCta("Skip");
  }

  changeStep(newStep: number) {
    if (newStep !== this.step) {
      if (newStep > this.step) {
        this.shouldAnimateInFromRight = true;
      } else {
        this.shouldAnimateInFromLeft = true;
      }
      this.step = newStep;
      this.windowService.setTimeout(() => {
        this.shouldAnimateInFromRight = false;
        this.shouldAnimateInFromLeft = false;
      }, 400);
    }
  }
}
